/**
 * A message passing based quill editor
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import LoadingOverlay from 'react-loading-overlay';
import { useIntl } from 'react-intl';
import { makeStyles, Tooltip, useMediaQuery, useTheme } from '@material-ui/core'
import { pushMessage } from '../../utils/MessageBusUtils';
import _ from 'lodash';
import VideoDialog from './CustomUI/VideoDialog';
import { embeddifyVideoLink } from './Utilities/VideoUtils';
import LinkDialog from './CustomUI/LinkDialog';
import Quill from 'quill';
import CustomQuillClipboard from './CustomQuillClipboard';
import NoOpUploader from './NoOpUploader';
import QuillTableUI from 'quill-table-ui';
import QuillS3ImageUploader from './QuillS3ImageUploader';
import ImageResize from 'quill-image-resize-module-withfix';
import QuillMention from 'quill-mention-uclusion';
import CustomCodeBlock from './CustomCodeBlock';
import PropTypes from 'prop-types';
import { getNameForUrl, getUrlForTicketPath } from '../../utils/marketIdPathFunctions';
import ImageBlot from './ImageBlot';
import DividerBlot from './DividerBlot';
import QuillEditorRegistry from './QuillEditorRegistry';
import {
  createEditor,
  getBoundsId
} from './Utilities/CoreUtils';
import { isTicketPath } from '../../contexts/TicketContext/ticketIndexContextHelper';
import { ticketContextHack } from '../../contexts/TicketContext/TicketIndexContext';
import { marketsContextHack } from '../../contexts/MarketsContext/MarketsContext';
import { commentsContextHack } from '../../contexts/CommentsContext/CommentsContext';
import { MyLink } from './Utilities/LinkUtils';
import { getLinkTargetName } from '../../utils/marketIdPathFunctions';
import { listStart, ListStartKeyboard } from './Utilities/listStart';
import { useHistory } from 'react-router';
import { handleRichTextLinkClick } from '../../utils/windowUtils';

// https://github.com/derrickpelletier/react-loading-overlay/pull/57
LoadingOverlay.propTypes = undefined;
Quill.debug('error');
// install our filtering paste module, and disable the uploader
Quill.register('modules/clipboard', CustomQuillClipboard, true);
Quill.register('modules/uploader', NoOpUploader, true);
Quill.register(ImageBlot);
Quill.register('modules/tableUI', QuillTableUI);
Quill.register('modules/s3Upload', QuillS3ImageUploader);
Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/mention', QuillMention);
Quill.register(CustomCodeBlock, true);
Quill.register(MyLink);
Quill.register(DividerBlot);
Quill.register(listStart);
Quill.register('modules/keyboard', ListStartKeyboard, true);
// Custom toolbar formats render a blank button unless given an icon, so supply
// one for the divider control here (once, at module load).
const quillIcons = Quill.import('ui/icons');
quillIcons['divider'] = '<svg viewBox="0 0 18 18"><line class="ql-stroke" x1="3" y1="9" x2="15" y2="9"></line></svg>';
// Trash icon for the custom "clear the editor" toolbar button (T-all-2168 / S-1).
quillIcons['clear'] = '<svg viewBox="0 0 24 24"><path class="ql-fill" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>';
const useStyles = makeStyles(
  theme => {
    return {
      root: {
        '& .ql-container.ql-snow': {
          fontFamily: theme.typography.fontFamily,
          fontSize: 15,
          border: 0
        },
        '& .ql-editor': {
          paddingLeft: 0
        },
      },
      nothing: {}
    };
  },
  { name: 'QuillEditorClasses' }
);

function QuillEditor2 (props) {

  const {
    id,
    cssId,
    value = '',
    uploadDisabled = false,
    noToolbar = false,
    simple = false,
    marketId,
    mentionsAllowed = false,
    mentionDenotationChars,
    placeholder = '',
    noOverflow,
    maxHeight,
    backgroundColor,
    isWhiteText = false,
    buttons = React.Fragment
  } = props;
  const useCssId = cssId || id;
  const classes = useStyles();
  const containerRef = useRef();
  const boxRef = useRef();
  const toolbarRef = useRef();
  const [uploadInProgress, setUploadInProgress] = useState(false);
  // S-all-321: a short code link reads as its code, so the target's current title is
  // shown on hover. A Quill blot is plain DOM built outside React, so the tooltip lives
  // here, on the component that owns the rendered body, and is anchored to whichever
  // link is hovered. That is what makes it the application's own tooltip - below the
  // link and centered on it - rather than the browser's native title tooltip, which
  // anchors at the pointer's x position and clips its own border (C-all-2098).
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredLinkName, setHoveredLinkName] = useState(undefined);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const intl = useIntl();
  const history = useHistory();
  const theme = useTheme();
  const boundsId = getBoundsId(useCssId);
  const mobileLayout = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * The UI for videos is quite poor, so we need
   * to replace it with ours
   */
  function createVideoUi (id) {
    return (
      <VideoDialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        onSave={(link) => {
          const { editor } = QuillEditorRegistry.getEditor(id);
          const embedded = embeddifyVideoLink(link);
          editor.format('video', embedded);
        }}
      />
    );
  }

  /**
   * The UI for links is also quite poor, so we need
   * to replace it with ours
   */
  function createLinkUi (id) {
    return (
      <LinkDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onSave={(link) => {
          const { editor } = QuillEditorRegistry.getEditor(id);
          // if they haven't got anything selected, just get the current
          // position and insert the url as the text,
          // otherwise just format the current selection as a link
          const selected = editor.getSelection(true);
          let url = link;
          const urlParts = new URL(url);
          if (isTicketPath(urlParts.pathname)) {
            const urlFromTicket = getUrlForTicketPath(urlParts.pathname, ticketContextHack, marketsContextHack,
              commentsContextHack);
            if (urlFromTicket) {
              url = urlFromTicket;
            }
          }
          //do we have nothing selected i.e. a zero length selection?
          if (selected.length === 0) {
            const index = selected ? selected.index : 0; // no position? do it at the front
            // if so, the selection is just the cursor position, so insert our new text there
            const name = getNameForUrl(link);
            editor.insertText(index, _.isEmpty(name) ? url : name, 'link', url, 'user');
            //refocus the editor because for some reason it moves to the top during insert
          } else {
            editor.format('link', url);
            // Remove selection or typing will erase the link text
            editor.setSelection(selected.index + selected.length, 0);
          }
        }}
      />
    );
  }

  function onS3Upload (metadatas) {
    const newUploads = [...uploadedFiles, ...metadatas];
    setUploadedFiles(newUploads);
    pushMessage(`editor-${id}`, { type: 'uploads', newUploads });
  }
  // T-all-2448: onS3Upload closes over uploadedFiles so its identity changes every render, which
  // made editorCreator (and its effect) run on every render; the ref keeps the upload handler
  // fresh behind a stable identity
  const onS3UploadRef = useRef();
  onS3UploadRef.current = onS3Upload;
  const stableOnS3Upload = useCallback((metadatas) => onS3UploadRef.current(metadatas), []);

  // bridge our fonts in from the theme;
  const editorStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    color: isWhiteText ? 'white' : 'black',
    overflowX: 'hidden',
    backgroundColor: backgroundColor || (noToolbar ? undefined : 'white'),
    minHeight: noToolbar ? undefined : '8rem'
  };

  const containerStyle = {
    maxWidth: '100%',
    zIndex: '2',
    borderTop: '1px solid lightgrey',
  };

  const containerReadOnlyStyle = {
    maxWidth: '100%',
    zIndex: '2'
  };

  // callback wrapper. Really should
  // resolve the deps issue with create editor
  const editorCreator = useCallback(() => {
    const idReady = id != null;
    const containersReady = containerRef.current != null && boxRef.current != null;
    const ready = containersReady && idReady;
    if (ready) {
      // creating editor
      const editorConfig = {
        scrollingContainer: `#${useCssId}scroll`,
        boxRef,
        containerRef,
        toolbarRef,
        marketId,
        layout: mobileLayout,
        noToolbar,
        onS3Upload: stableOnS3Upload,
        setUploadInProgress,
        setVideoDialogOpen,
        setLinkDialogOpen,
        simple,
        uploadDisabled,
        mentionsAllowed,
        mentionDenotationChars,
        boundsId,
        placeholder,
        value
      };
      createEditor(id, noToolbar ? value : undefined, editorConfig, false);
    }
    // This is probably a bad idea, but the create should be fine
    // due to the checks above (missing createEditor dep)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileLayout, noToolbar, stableOnS3Upload, setUploadInProgress, setVideoDialogOpen, setLinkDialogOpen, simple,
    uploadDisabled, mentionsAllowed, mentionDenotationChars, boundsId, boxRef, marketId, containerRef, placeholder,
    value
  ]);

  useEffect(() => {
    editorCreator();
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (!boxRef || !boxRef.current) {
        // using boxRef to know if unmounted or not - don't want to remove if still mounted as will lose typing
        QuillEditorRegistry.remove(id);
      }
    };
  }, [id, editorCreator]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const onOver = (event) => {
      const link = event.target?.closest?.('a');
      if (!link || !container.contains(link)) {
        return;
      }
      const name = getLinkTargetName(link.getAttribute('href'));
      if (!name) {
        return;
      }
      setHoveredLinkName(name);
      setHoveredLink(link);
    };
    const onOut = (event) => {
      const link = event.target?.closest?.('a');
      if (!link) {
        return;
      }
      // mouseout also fires moving between a link's own descendants, so only clear
      // when the pointer has actually left that link.
      if (event.relatedTarget && link.contains(event.relatedTarget)) {
        return;
      }
      setHoveredLink(null);
    };
    container.addEventListener('mouseover', onOver);
    container.addEventListener('mouseout', onOut);
    return () => {
      container.removeEventListener('mouseover', onOver);
      container.removeEventListener('mouseout', onOut);
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: maxHeight || (noOverflow ? undefined : '50vh'),
        // Narrower, GMail-like composer width (T-all-2168 / Q-all-140 O-1); only
        // for the editable composer, not the read-only (noToolbar) display.
        maxWidth: noToolbar ? undefined : '680px',
      }}
    >
      <Tooltip
        open={!!hoveredLink}
        title={hoveredLinkName || ''}
        placement="bottom"
        PopperProps={{ anchorEl: hoveredLink }}
      >
        <span />
      </Tooltip>
      <div
        id={`${useCssId}scroll`}
        style={{
          flex: noOverflow ? undefined : 1,
          overflowY: noOverflow ? undefined : 'auto',
          minHeight: noOverflow ? undefined : 0,
        }}
      >
        {createVideoUi(id)}
        {createLinkUi(id)}
        <div
          ref={containerRef}
          className={noToolbar ? classes.root : classes.nothing}
          style={noToolbar ? containerReadOnlyStyle : containerStyle}
          id={useCssId}
          onClick={noToolbar ? (event) => handleRichTextLinkClick(event, history) : undefined}
        >
          {noToolbar && (
            <div ref={boxRef} id={boundsId} style={editorStyle}/>
          )}
          {!noToolbar && (
            <LoadingOverlay
              active={uploadInProgress}
              spinner
              className="editor-wrapper"
              text={intl.formatMessage({ id: 'quillEditorUploadInProgress' })}
            >
              <div ref={boxRef} id={boundsId} style={editorStyle}/>
              {buttons}
            </LoadingOverlay>
          )}
        </div>
      </div>
      {!noToolbar && (
        <div ref={toolbarRef} style={{ flexShrink: 0, position: 'relative', zIndex: 8 }} />
      )}
    </div>
  );
}

QuillEditor2.propTypes = {
  marketId: PropTypes.string,
  value: PropTypes.string,
  onStoreChange: PropTypes.func,
  placeholder: PropTypes.string,
  uploadDisabled: PropTypes.bool,
  noToolbar: PropTypes.bool,
  id: PropTypes.string.isRequired,
  simple: PropTypes.bool,
  mentionsAllowed: PropTypes.bool,
  mentionDenotationChars: PropTypes.arrayOf(PropTypes.string),
  dontManageState: PropTypes.bool
};

// B-all-569 (Q-all-449 O-1): an editable editor is insulated from its ancestors after mount.
// The live Quill instance is outside React in QuillEditorRegistry and the wrapper's only job
// after creation is holding the container div, so no prop change from a re-rendering host may
// reach it; anything that must change mid-edit goes over the editor message bus
// (resetEditor with configOverrides). Read-only (noToolbar) instances keep shallow comparison
// because recreating on a value change is how they display fresh content.
function insulatedPropsAreEqual(prevProps, nextProps) {
  if (prevProps.id !== nextProps.id) {
    return false;
  }
  if (!prevProps.noToolbar && !nextProps.noToolbar) {
    return true;
  }
  const prevKeys = Object.keys(prevProps);
  return prevKeys.length === Object.keys(nextProps).length &&
    prevKeys.every((key) => prevProps[key] === nextProps[key]);
}

export default React.memo(QuillEditor2, insulatedPropsAreEqual);
